export interface ErrorMetadata {
    [key: string]: unknown;
}

export class BaseError extends Error {
    public readonly code?: string;
    public readonly metadata?: ErrorMetadata;
    public readonly cause?: Error;

    constructor(
        message: string,
        options?: {
            code?: string;
            metadata?: ErrorMetadata;
            cause?: Error;
        },
    ) {
        super(message);
        this.name = this.constructor.name;
        this.code = options?.code;
        this.metadata = options?.metadata;
        this.cause = options?.cause;
    }

    static async fromError(
        error: Error,
        options?: {
            code?: string;
            metadata?: ErrorMetadata;
            context?: string;
        },
    ): Promise<BaseError> {
        const baseError = Object.create(BaseError.prototype);
        baseError.message = error.message;
        baseError.name = error.name;
        baseError.stack = error.stack;
        baseError.code = options?.code;
        baseError.metadata = options?.metadata;

        return baseError;
    }
}

export class APIError extends BaseError {
    public readonly status?: number;
    public readonly endpoint?: string;
    public readonly method?: string;

    constructor(
        message: string,
        options?: {
            status?: number;
            endpoint?: string;
            method?: string;
            code?: string;
            metadata?: ErrorMetadata;
            cause?: Error;
        },
    ) {
        super(message, {
            code: options?.code || `API_ERROR_${options?.status || 'UNKNOWN'}`,
            metadata: {
                ...options?.metadata,
                status: options?.status,
                endpoint: options?.endpoint,
                method: options?.method,
            },
            cause: options?.cause,
        });

        this.status = options?.status;
        this.endpoint = options?.endpoint;
        this.method = options?.method;
    }

    static fromResponse(response: Response, endpoint?: string, method?: string): APIError {
        return new APIError(`API request failed: ${response.status} ${response.statusText}`, {
            status: response.status,
            endpoint: endpoint || response.url,
            method: method || 'GET',
        });
    }
}

export class ClientError extends BaseError {
    public readonly component?: string;
    public readonly userAgent?: string;
    public readonly url?: string;

    constructor(
        message: string,
        options?: {
            component?: string;
            code?: string;
            metadata?: ErrorMetadata;
            cause?: Error;
        },
    ) {
        const clientMetadata = {
            ...options?.metadata,
            component: options?.component,
            userAgent: typeof window !== 'undefined' ? window.navigator?.userAgent : undefined,
            url: typeof window !== 'undefined' ? window.location?.href : undefined,
            timestamp: new Date().toISOString(),
        };

        super(message, {
            code: options?.code || 'CLIENT_ERROR',
            metadata: clientMetadata,
            cause: options?.cause,
        });

        this.component = options?.component;
        this.userAgent = clientMetadata.userAgent;
        this.url = clientMetadata.url;
    }
}
